
import React, { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import PricingTable from '@/components/secret-pricing/PricingTable';
import PricingHeader from '@/components/secret-pricing/PricingHeader';
import SearchBox from '@/components/secret-pricing/SearchBox';
import { updatedPriceData } from '@/data/updatedPriceData';
import EmptyState from '@/components/secret-pricing/EmptyState';
import FileUploadSection from '@/components/secret-pricing/FileUploadSection';
import ErrorDialog from '@/components/secret-pricing/ErrorDialog';
import DebugDialog from '@/components/secret-pricing/DebugDialog';
import { 
  createPricingTemplate, 
  parseExcelPricing, 
  PriceData,
  savePriceDataToStorage,
  loadPriceDataFromStorage
} from '@/utils/excelUtils';

// Storage key for localStorage
const PRICE_DATA_STORAGE_KEY = 'aurora-secret-pricing-data';

const SecretPricing = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const { toast } = useToast();
  
  // Load data from localStorage or use default on component mount
  useEffect(() => {
    // Set loading state while retrieving data
    setIsLoading(true);
    
    try {
      const loadedData = loadPriceDataFromStorage(PRICE_DATA_STORAGE_KEY, updatedPriceData);
      setPriceData(loadedData);
      
      // Display loaded data count in a toast
      toast({
        title: "Data Loaded",
        description: `Loaded ${loadedData.length} products from storage`,
      });
      
    } catch (error) {
      console.error("Error during initial data load:", error);
      setPriceData(updatedPriceData);
      
      toast({
        title: "Error Loading Data",
        description: "Using default data instead",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);
  
  // Filter price data based on search term
  const filteredPriceData = priceData.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      item.productName.toLowerCase().includes(searchTermLower) ||
      item.productId.toLowerCase().includes(searchTermLower) ||
      item.msrp.toLowerCase().includes(searchTermLower)
    );
  });
  
  useEffect(() => {
    // Show a toast when the page loads
    toast({
      title: "Secret Pricing",
      description: `You accessed the secret price list with ${priceData.length} products`,
    });
    
    console.log("Total products:", priceData.length);
    console.log("Filtered products:", filteredPriceData.length);
    
    // Set debug info with current product count
    setDebugInfo(JSON.stringify({
      totalProducts: priceData.length,
      filteredProducts: filteredPriceData.length,
      sampleData: priceData.slice(0, 3)
    }, null, 2));
    
  }, [toast, filteredPriceData.length, priceData.length]);

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log("File selected:", file.name, file.size, file.type);
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      console.log("File converted to ArrayBuffer, size:", arrayBuffer.byteLength);
      
      const result = parseExcelPricing(arrayBuffer);
      console.log("Parsing complete, records found:", result.length);
      
      if (result.length === 0) {
        throw new Error("No visible pricing data found in the Excel file");
      }
      
      // Update the price data state
      setPriceData(result);
      
      // Store the data in localStorage for persistence
      savePriceDataToStorage(PRICE_DATA_STORAGE_KEY, result);
      
      // Set debug info
      setDebugInfo(JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        recordsLoaded: result.length,
        firstFewRecords: result.slice(0, 5)
      }, null, 2));
      
      toast({
        title: "Success",
        description: `Loaded ${result.length} visible products from ${file.name}`,
      });
      
    } catch (error) {
      console.error("Excel parsing error:", error);
      setErrorMessage(error instanceof Error ? error.message : "Unknown error processing Excel file");
      setShowErrorDialog(true);
    } finally {
      setIsLoading(false);
      
      // Clear the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Download template
  const downloadTemplate = () => {
    createPricingTemplate();
  };

  // Clear loaded data
  const clearData = () => {
    setPriceData(updatedPriceData);
    localStorage.removeItem(PRICE_DATA_STORAGE_KEY);
    toast({
      title: "Data Cleared",
      description: "Pricing data has been reset to defaults",
    });
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <PricingHeader 
        title="Secret Pricing"
        description={`You accessed this page by clicking the Aurora logo 5 times on the home page. Showing ${filteredPriceData.length} of ${priceData.length} visible products.`}
      />
      
      <FileUploadSection 
        isLoading={isLoading}
        handleFileUpload={handleFileUpload}
        downloadTemplate={downloadTemplate}
        clearData={clearData}
        priceDataLength={priceData.length}
        setShowDebug={setShowDebug}
      />
      
      <SearchBox 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        totalCount={priceData.length}
        filteredCount={filteredPriceData.length}
      />

      <div className="border rounded-md shadow-sm">
        {filteredPriceData.length > 0 ? (
          <PricingTable 
            priceData={filteredPriceData}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        ) : (
          <EmptyState />
        )}
      </div>
      
      {/* Error Dialog */}
      <ErrorDialog 
        open={showErrorDialog} 
        onOpenChange={setShowErrorDialog} 
        errorMessage={errorMessage} 
      />
      
      {/* Debug Dialog */}
      <DebugDialog 
        open={showDebug} 
        onOpenChange={setShowDebug} 
        debugInfo={debugInfo} 
      />
    </div>
  );
};

export default SecretPricing;
